// TEAM-KPI queries (founder-approved 2026-10-21): a team's KPI set, its scorecard for a week, and
// every viewable team's score (the chart's team chip, Cadence's team scorecards). Keys sit under
// `["company", co]`, so every KPI write's invalidation refreshes them too. Mounted only behind
// `useFeature("kpi")`.

import type { OperationOutput } from "@companyos/sdk";
import { ApiError } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type TeamMetricSet = OperationOutput<"teams.metrics">;
export type TeamMetricLine = TeamMetricSet["data"][number];
export type TeamScore = OperationOutput<"scorecards.teams">["data"][number];
export type TeamScorecard = OperationOutput<"teams.scorecard">;

export const teamKpiKeys = {
  set: (co: string, team: string) => ["company", co, "team", team, "metrics"] as const,
  scorecard: (co: string, team: string, week: string) =>
    ["company", co, "team", team, "scorecard", week] as const,
  scores: (co: string, week: string) => ["company", co, "team-scores", week] as const,
};

/** The team's KPI set and whether the caller may change it (its lead or a manager). */
export const teamMetricsQuery = (api: Api, co: string, team: string) =>
  queryOptions({
    queryKey: teamKpiKeys.set(co, team),
    queryFn: () => api.client.teams.metrics({ params: { co, id: team } }),
  });

/** The team's scorecard for `week`. */
export const teamScorecardQuery = (api: Api, co: string, team: string, week: string) =>
  queryOptions({
    queryKey: teamKpiKeys.scorecard(co, team, week),
    queryFn: () =>
      api.client.teams.scorecard({ params: { co, id: team }, query: { period: week } }),
  });

/**
 * Every team the caller may view that has KPIs, with its score for `week`. A caller the API
 * refuses (no `kpi` add-on reach, a key) reads as no teams rather than an error.
 */
export const teamScoresQuery = (api: Api, co: string, week: string) =>
  queryOptions({
    queryKey: teamKpiKeys.scores(co, week),
    queryFn: async () => {
      try {
        return (await api.client.scorecards.teams({ params: { co }, query: { period: week } }))
          .data;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return [];
        throw e;
      }
    },
  });
